<template>
  <div v-if="mergedFile">
    <b-alert variant="secondary" :show="true">
      <font-awesome-icon icon="info-circle"/>
      The interface shows the current machine file information on the left and the proposed changes on the right. <br>
      Choose to keep or replace each value by clicking the corresponding text.
      Matching values are automatically marked as selected and expand to the full row width.
    </b-alert>
    <machine-file-merger-data :merged-data="mergedFile.static" :machine-data="machineFile.static" :master-data="masterFile.static"
                              :section="sections.static" @onChange="(complete) => sectionChanged(sections.static, complete)"/>
    <machine-file-merger-data :merged-data="mergedFile.fixed" :machine-data="machineFile.fixed" :master-data="masterFile.fixed"
                              :section="sections.weight" @onChange="(complete) => sectionChanged(sections.weight, complete)"/>
    <machine-file-merger-data :merged-data="mergedFile.fixed?.process" :machine-data="machineFile.fixed?.process" :master-data="masterFile.fixed?.process"
                              :section="sections.process" @onChange="(complete) => sectionChanged(sections.process, complete)"/>
    <machine-file-merger-data :merged-data="mergedFile.fixed?.media?.topDeck" :machine-data="machineFile.fixed?.media?.topDeck" :master-data="masterFile.fixed?.media?.topDeck"
                              :section="sections.mediaTop" @onChange="(complete) => sectionChanged(sections.mediaTop, complete)"/>
    <machine-file-merger-data :merged-data="mergedFile.fixed?.media?.upperMidDeck" :machine-data="machineFile.fixed?.media?.upperMidDeck" :master-data="masterFile.fixed?.media?.upperMidDeck"
                              :section="sections.mediaUpperMid" @onChange="(complete) => sectionChanged(sections.mediaUpperMid, complete)"/>
    <machine-file-merger-data :merged-data="mergedFile.fixed?.media?.lowerMidDeck" :machine-data="machineFile.fixed?.media?.lowerMidDeck" :master-data="masterFile.fixed?.media?.lowerMidDeck"
                              :section="sections.mediaLowerMid" @onChange="(complete) => sectionChanged(sections.mediaLowerMid, complete)"/>
    <machine-file-merger-data :merged-data="mergedFile.fixed?.media?.bottomDeck" :machine-data="machineFile.fixed?.media?.bottomDeck" :master-data="masterFile.fixed?.media?.bottomDeck"
                              :section="sections.mediaBottom" @onChange="(complete) => sectionChanged(sections.mediaBottom, complete)"/>
    <machine-file-merger-data :merged-data="mergedFile.fixed" :machine-data="machineFile.fixed" :master-data="masterFile.fixed"
                              :section="sections.suspension" @onChange="(complete) => sectionChanged(sections.suspension, complete)"/>
    <machine-file-merger-data :merged-data="mergedFile.fixed" :machine-data="machineFile.fixed" :master-data="masterFile.fixed"
                              :section="sections.lubrication" @onChange="(complete) => sectionChanged(sections.lubrication, complete)"/>
    <machine-file-merger-data :merged-data="mergedFile.fixed?.screenSuspensionFeedDischarge" :machine-data="machineFile.fixed?.screenSuspensionFeedDischarge" :master-data="masterFile.fixed?.screenSuspensionFeedDischarge"
                              :section="sections.screenSuspensionFeedDischarge" @onChange="(complete) => sectionChanged(sections.screenSuspensionFeedDischarge, complete)"/>
    <machine-file-merger-data :merged-data="mergedFile.fixed?.isolationFrameFeedDischarge" :machine-data="machineFile.fixed?.isolationFrameFeedDischarge" :master-data="masterFile.fixed?.isolationFrameFeedDischarge"
                              :section="sections.isolationFrameFeedDischarge" @onChange="(complete) => sectionChanged(sections.isolationFrameFeedDischarge, complete)"/>
    <machine-file-merger-data :merged-data="mergedFile.fixed?.motor" :machine-data="machineFile.fixed?.motor" :master-data="masterFile.fixed?.motor"
                              :section="sections.motor" @onChange="(complete) => sectionChanged(sections.motor, complete)"/>
    <machine-file-merger-data :merged-data="mergedFile.fixed?.motor?.motorA" :machine-data="machineFile.fixed?.motor?.motorA" :master-data="masterFile.fixed?.motor?.motorA"
                              :section="sections.motorA" @onChange="(complete) => sectionChanged(sections.motorA, complete)"/>
    <machine-file-merger-data :merged-data="mergedFile.fixed?.motor?.motorB" :machine-data="machineFile.fixed?.motor?.motorB" :master-data="masterFile.fixed?.motor?.motorB"
                              :section="sections.motorB" @onChange="(complete) => sectionChanged(sections.motorB, complete)"/>
    <machine-file-merger-data :merged-data="mergedFile.fixed" :machine-data="machineFile.fixed" :master-data="masterFile.fixed"
                              :section="sections.revision" @onChange="(complete) => sectionChanged(sections.revision, complete)"/>
  </div>
</template>

<script>
import MachineFileMergerData from '@/components/machine_file/merger/Data.vue'
import { emptyMachineFile, machineFileStructure } from '@/utils/MachineFile'
import Vue from 'vue'
import _ from 'lodash'
import { MachineFile } from '@/screencheck_portal_api/src'

export default {
  name: 'MachineFileMergerOverview',
  components: { MachineFileMergerData },
  props: {
    masterFile: {
      type: MachineFile,
      required: true,
    },
    machineFile: {
      type: MachineFile,
      required: true,
    },
  },
  data () {
    return {
      mergedFile: undefined,
      sections: {
        static: { label: 'Static Data', keys: [], complete: false },
        weight: { label: 'Weight Data', keys: [], complete: false },
        process: { label: 'Application Data', keys: [], complete: false },
        mediaTop: { label: 'Media Data - Top Deck', keys: [], complete: false },
        mediaUpperMid: { label: 'Media Data - Upper Mid Deck', keys: [], complete: false },
        mediaLowerMid: { label: 'Media Data - Lower Mid Deck', keys: [], complete: false },
        mediaBottom: { label: 'Media Data - Bottom Deck', keys: [], complete: false },
        suspension: { label: 'Suspension Data', keys: [], complete: false },
        lubrication: { label: 'Lubrication Data', keys: [], complete: false },
        screenSuspensionFeedDischarge: { label: 'Screen Suspension Feed Discharge', keys: [], complete: false },
        isolationFrameFeedDischarge: { label: 'Isolation Frame Feed Discharge', keys: [], complete: false },
        motor: { label: 'Motor Data', keys: [], complete: false },
        motorA: { label: 'Motor A Data', keys: [], complete: false },
        motorB: { label: 'Motor B Data', keys: [], complete: false },
        revision: { label: 'Revision Data', keys: [], complete: false },
      },
    }
  },
  mounted () {
    const template = emptyMachineFile()
    this.initializeKeys(template)
    this.autoMerge(template)
    this.$emit('onChange', this.mergedFile, this.allSelected)
  },
  computed: {
    allSelected () {
      return Object.values(this.sections).filter(section => section.keys.length > 0).map(section => section.complete)
    }
  },
  methods: {
    autoMerge (template) {
      this.mergedFile = machineFileStructure()
      this.cleanupRecursive(this.masterFile, template)
      this.cleanupRecursive(this.machineFile, template)
      this.recursiveMerge(this.masterFile, this.machineFile, template, this.mergedFile)
      this.mergedFile.uuid = null
      this.mergedFile.version = template.version
      this.mergedFile.timestamp = null
      this.mergedFile.machineId = null
      this.mergedFile.revision = null
      this.mergedFile.derivedFromMachineId = null
      this.mergedFile.derivedFromRevision = null
    },
    cleanupRecursive (obj, template) {
      // Recursively iterate over all fields. Add fields from the template that are missing in the object.
      for (const key in template) {
        if (obj[key] === undefined || obj[key] === null || obj[key] === '') {
          Vue.set(obj, key, _.cloneDeep(template[key]))
        } else if (template[key] !== null && typeof template[key] === 'object' && !Array.isArray(template[key])) {
          this.cleanupRecursive(obj[key], template[key])
        }
      }
    },
    recursiveMerge (obj1, obj2, template, merged) {
      for (const key in template) {
        if (key in obj1 && key in obj2) {
          if (template[key] !== null && typeof template[key] === 'object' && !Array.isArray(template[key])) {
            this.recursiveMerge(obj1[key], obj2[key], template[key], merged[key])
          } else if (_.isEqual(obj1[key], obj2[key])) {
            Vue.set(merged, key, _.cloneDeep(obj1[key]))
          }
        }
      }
    },
    sectionChanged (section, complete) {
      section.complete = complete
      this.$emit('onChange', this.mergedFile, this.allSelected)
    },
    filterKeysByWord (object, keyword) {
      const filteredObject = {}
      for (const key in object) {
        if (key.includes(keyword)) {
          filteredObject[key] = object[key]
        }
      }
      return Object.keys(filteredObject)
    },
    filterSuspensionKeys (object) {
      const filteredObject = {}
      const ignoreKeys = [
        'screensuspensionfeeddischarge',
        'isolationframefeeddischarge',
        'weight',
        'weightmethod',
        'lubricationspec',
        'greasingpowerunit',
        'gadrawingnumberrevision',
        'sparepartbooknumberrevision',
        'instructionmanualnumberrevision'
      ] // Ignore keys shown in other sections
      for (const key in object) {
        if (object[key] === null && !ignoreKeys.includes(key.toLowerCase())) {
          filteredObject[key] = object[key]
        }
      }
      return Object.keys(filteredObject)
    },
    filterLubricationKeys (object) {
      const filteredObject = {}
      for (const key in object) {
        if (object[key] === null && (
            key.toLowerCase().includes('lubrication') ||
            key.toLowerCase().includes('greasing'))) {
          filteredObject[key] = object[key]
        }
      }
      return Object.keys(filteredObject)
    },
    filterKeys (object) {
      const filteredObject = {}
      for (const key in object) {
        if ((object[key] === null || Array.isArray(object[key]))) {
          filteredObject[key] = object[key]
        }
      }
      return Object.keys(filteredObject)
    },
    initializeKeys (template) {
      this.sections.static.keys = [...new Set([
        ...Object.keys(template.static)
      ])]
      this.sections.weight.keys = [...new Set([
        ...this.filterKeysByWord(template.fixed, 'weight')
      ])]
      this.sections.process.keys = [...new Set([
        ...Object.keys(template.fixed.process)
      ])]
      this.sections.mediaTop.keys = [...new Set([
        ...Object.keys(template.fixed.media.topDeck)
      ])]
      this.sections.mediaUpperMid.keys = [...new Set([
        ...Object.keys(template.fixed.media.upperMidDeck)
      ])]
      this.sections.mediaLowerMid.keys = [...new Set([
        ...Object.keys(template.fixed.media.lowerMidDeck)
      ])]
      this.sections.mediaBottom.keys = [...new Set([
        ...Object.keys(template.fixed.media.bottomDeck)
      ])]
      this.sections.suspension.keys = [...new Set([
        ...this.filterSuspensionKeys(template.fixed)
      ])]
      this.sections.lubrication.keys = [...new Set([
        ...this.filterLubricationKeys(template.fixed)
      ])]
      this.sections.screenSuspensionFeedDischarge.keys = [...new Set([
        ...Object.keys(template.fixed.screenSuspensionFeedDischarge)
      ])]
      this.sections.isolationFrameFeedDischarge.keys = [...new Set([
        ...Object.keys(template.fixed.isolationFrameFeedDischarge)
      ])]
      this.sections.motor.keys = [...new Set([
        ...this.filterKeys(template.fixed.motor)
      ])]
      this.sections.motorA.keys = [...new Set([
        ...Object.keys(template.fixed.motor.motorA)
      ])]
      this.sections.motorB.keys = [...new Set([
        ...Object.keys(template.fixed.motor.motorB)
      ])]
      this.sections.revision.keys = [...new Set([
        ...this.filterKeysByWord(template.fixed, 'Revision')
      ])]

      this.clearEmptySections()
    },
    clearEmptySections () {
      this.clearSectionIfEmpty(this.machineFile.static, this.masterFile.static, this.sections.static)
      this.clearSectionIfEmpty(this.machineFile.fixed, this.masterFile.fixed, this.sections.weight)
      this.clearSectionIfEmpty(this.machineFile.fixed?.process, this.masterFile.fixed?.process, this.sections.process)
      this.clearSectionIfEmpty(this.machineFile.fixed?.media?.topDeck, this.masterFile.fixed?.media?.topDeck, this.sections.mediaTop)
      this.clearSectionIfEmpty(this.machineFile.fixed?.media?.upperMidDeck, this.masterFile.fixed?.media?.upperMidDeck, this.sections.mediaUpperMid)
      this.clearSectionIfEmpty(this.machineFile.fixed?.media?.lowerMidDeck, this.masterFile.fixed?.media?.lowerMidDeck, this.sections.mediaLowerMid)
      this.clearSectionIfEmpty(this.machineFile.fixed?.media?.bottomDeck, this.masterFile.fixed?.media?.bottomDeck, this.sections.mediaBottom)
      this.clearSectionIfEmpty(this.machineFile.fixed, this.masterFile.fixed, this.sections.suspension)
      this.clearSectionIfEmpty(this.machineFile.fixed, this.masterFile.fixed, this.sections.lubrication)
      this.clearSectionIfEmpty(this.machineFile.fixed?.screenSuspensionFeedDischarge, this.masterFile.fixed?.screenSuspensionFeedDischarge, this.sections.screenSuspensionFeedDischarge)
      this.clearSectionIfEmpty(this.machineFile.fixed?.isolationFrameFeedDischarge, this.masterFile.fixed?.isolationFrameFeedDischarge, this.sections.isolationFrameFeedDischarge)
      this.clearSectionIfEmpty(this.machineFile.fixed?.motor, this.masterFile.fixed?.motor, this.sections.motor)
      this.clearSectionIfEmpty(this.machineFile.fixed?.motor?.motorA, this.masterFile.fixed?.motor?.motorA, this.sections.motorA)
      this.clearSectionIfEmpty(this.machineFile.fixed?.motor?.motorB, this.masterFile.fixed?.motor?.motorB, this.sections.motorB)
      this.clearSectionIfEmpty(this.machineFile.fixed, this.masterFile.fixed, this.sections.revision)
    },
    isEmptyIsh (obj) {
      return obj === undefined || obj === null || obj === '' || (Array.isArray(obj) && obj.length === 0)
    },
    clearSectionIfEmpty (machineFileSection, masterFileSection, section) {
      for (const key of section.keys) {
        if (!this.isEmptyIsh(machineFileSection[key]) || !this.isEmptyIsh(masterFileSection[key])) {
          return
        }
      }
      section.keys = []
    }
  },
}
</script>
